import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OrganizationAvatar } from "@/components/organization-avatar";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import { AppShell } from "@/components/layouts/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileDown, ChevronUp, ChevronDown, Trash2, Heart } from "lucide-react";
import type { Project, Ticket, ApiResponse } from "@mangelapp/shared";
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import { ProjectWeatherBadge } from "@/components/project-weather-badge";
import { useTranslation } from 'react-i18next';
import { apiClient } from "@/lib/api-client";
// Weather initialization removed - weather is now fetched on-demand when creating tickets/adding photos
import { useCurrentUser } from "@/hooks/use-current-user";
import { Calendar, Users, FileText, FolderOpen } from "lucide-react";
import { useCurrentOrganization } from "@/hooks/use-current-organization";
import { useProjectCurrency } from "@/hooks/use-project-currency";
import EditTicketInlineModal from "@/components/edit-ticket-inline-modal";
import { usePermissions } from "@/hooks/use-permissions";
import { getStatusColor } from "@/utils/color-utils";
import { usePriorities } from "@/hooks/use-priorities";
import { useLocation } from "wouter";
import { useDateFormat } from '@/utils/date-formatter';
import { HeaderSearchInput } from "@/components/layouts/header-slots";
import { getProjectCardStatusTranslationKey, useProjectStatuses } from "@/hooks/use-project-statuses";
import { AccessDenied } from "@/components/AccessDenied";
import { useDashboardColors } from "@/hooks/use-dashboard-colors";

// Removed unused Stats interface

interface ProjectTicketStats {
  projectId: string;
  total: number;
  overdue: number;
  statusCounts: Record<string, number>;
  totalLikes: number; // Gesamt-Likes für alle Tickets des Projekts
  likedTickets: number; // Anzahl Tickets mit mindestens 1 Like
  assignedToMe: number;
  overtime: number;
}

export default function Dashboard() {
  const { t, i18n } = useTranslation(['pages.dashboard', 'components.ticket', 'pages.listen', 'components.projectCard']);
  const { data: projectStatuses = [] } = useProjectStatuses();
  const { data: priorities = [] } = usePriorities();
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const [activeTab, setActiveTab] = useState("projekte");
  const [selectedStatusFilterId, setSelectedStatusFilterId] = useState<string | null>(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [location, setLocation] = useLocation();

  // Reset to projects tab when navigating to dashboard or when dashboard-reset event is dispatched
  useEffect(() => {
    const handleDashboardReset = () => {
      setActiveTab("projekte");
    };

    window.addEventListener('dashboard-reset', handleDashboardReset);
    return () => {
      window.removeEventListener('dashboard-reset', handleDashboardReset);
    };
  }, []);
  const { formatDate, formatDateTime } = useDateFormat();

  // Weather initialization removed - weather is now fetched on-demand when creating tickets/adding photos
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [showAccessDeniedDialog, setShowAccessDeniedDialog] = useState(false);
  const queryClient = useQueryClient();

  // Permissions
  const { hasPermission, permissionsLoading } = usePermissions();
  const canAccessDashboardProjectData =
    hasPermission('manage_projects') ||
    hasPermission('manage_all_projects') ||
    hasPermission('view_projects') ||
    hasPermission('view_all_projects');
  const canInteractDashboardTicketSummary =
    hasPermission('manage_projects') || hasPermission('manage_all_projects');

  // Current user (for ticket assignment)
  const { user: currentUser } = useCurrentUser();
  const { organizationId, isContractor } = useCurrentOrganization();

  const { data: dashboardColorsData } = useDashboardColors();
  const dashboardColors = dashboardColorsData?.colors ?? {};
  const projectsTabColor = dashboardColors.projects ?? '#e5e7eb';
  const assignedTabColor = dashboardColors.assignedToMe ?? '#9ca3af';
  const overtimeTabColor = dashboardColors.overtime ?? '#fbbf24';
  const overdueTabColor = dashboardColors.overdue ?? '#ef4444';
  const dueSoonTabColor = dashboardColors.dueSoon ?? '#60a5fa';

  // Fetch organization data for logo
  const { data: orgMeResponse } = useQuery({
    queryKey: ["/api/orgs/me", organizationId],
    queryFn: async () => {
      const url = organizationId
        ? `/api/orgs/me?organizationId=${encodeURIComponent(organizationId)}`
        : '/api/orgs/me';
      const res = await apiClient.customRequest(url);
      return res;
    },
    staleTime: 60_000,
    retry: false,
    enabled: !!organizationId,
  });
  const orgInfo = (orgMeResponse as any)?.data || null;
  const orgLogoUrl = orgInfo?.logoUrl || null;

  const { data: statsResponse, isLoading: statsLoading } = useQuery<ApiResponse<Record<string, any>>>({
    queryKey: ["/api/stats", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/stats?organizationId=${encodeURIComponent(organizationId)}` : "/api/stats";
      const response = await apiClient.customRequest<ApiResponse<Record<string, any>>>(url);
      return response;
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const stats = (statsResponse && statsResponse.success === true) ? statsResponse.data || {} : {};

  const { data: projectsResponse, isLoading: projectsLoading } = useQuery<ApiResponse<Project[]>>({
    queryKey: ["/api/projects", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/projects?organizationId=${encodeURIComponent(organizationId)}` : "/api/projects";
      const response = await apiClient.customRequest<ApiResponse<Project[]>>(url);
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  const projects = (projectsResponse && projectsResponse.success === true) ? projectsResponse.data || [] : [];
  const filteredProjects = useMemo(() => {
    const term = projectSearchTerm.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((project) => {
      const haystack = [
        project.name,
        project.projektNummer,
        project.beschreibung,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [projects, projectSearchTerm]);

  // Load tickets when tickets-related tabs OR projects tab is active (projects tab needs ticket stats)
  const ticketsTabs = ['meine-tickets', 'zugewiesene-tickets', 'ueberfaellige-tickets', 'bald-faellige-tickets', 'overtime-tickets'];
  const shouldLoadTickets = ticketsTabs.includes(activeTab) || activeTab === 'projekte';

  const { data: ticketsResponse, isLoading: ticketsLoading } = useQuery<ApiResponse<Ticket[]>>({
    queryKey: ["/api/tickets", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/tickets?organizationId=${encodeURIComponent(organizationId)}` : "/api/tickets";
      const response = await apiClient.customRequest<ApiResponse<Ticket[]>>(url);
      return response;
    },
    enabled: !!organizationId && shouldLoadTickets,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const allTickets = (ticketsResponse && ticketsResponse.success === true) ? ticketsResponse.data || [] : [];

  // Dashboard chips should not include tickets from demo projects.
  const demoProjectIdSet = useMemo(() => {
    return new Set(
      projects
        .filter((project: Project) => (project as any).isDemo === true)
        .map((project: Project) => project.id)
    );
  }, [projects]);

  const ticketsForChipCounts = useMemo(() => {
    if (demoProjectIdSet.size === 0) return allTickets;
    return allTickets.filter((ticket: Ticket) => !demoProjectIdSet.has(ticket.projectId));
  }, [allTickets, demoProjectIdSet]);

  const filteredTicketsByStatus = useMemo(() => {
    if (!selectedStatusFilterId) return allTickets;
    return allTickets.filter((ticket: Ticket) => (ticket as any).statusId === selectedStatusFilterId);
  }, [allTickets, selectedStatusFilterId]);

  // Only load status when tickets tabs are active (needed for completed status detection)
  const { data: statusResponse } = useQuery<ApiResponse<any[]>>({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiClient.customRequest<ApiResponse<any[]>>(url);
      return response;
    },
    enabled: !!organizationId && shouldLoadTickets,
    staleTime: 10 * 60 * 1000, // 10 minutes (status rarely changes)
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });

  const availableStatus = (statusResponse && statusResponse.success === true) ? statusResponse.data || [] : [];

  // Fetch gewerke and typen for display validation
  const { data: gewerkeResponse } = useQuery({
    queryKey: ["/api/gewerke", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` : "/api/gewerke";
      const response = await apiClient.customRequest(url);
      return response;
    },
    enabled: !!organizationId && shouldLoadTickets,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  const { data: typenResponse } = useQuery({
    queryKey: ["/api/typen", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/typen?organizationId=${encodeURIComponent(organizationId)}` : "/api/typen";
      const response = await apiClient.customRequest(url);
      return response;
    },
    enabled: !!organizationId && shouldLoadTickets,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  const gewerke = (gewerkeResponse as any)?.data || [];
  const typen = (typenResponse as any)?.data || [];

  // Helper to get status display name with validation
  const getStatusDisplayName = useCallback((status: string | null | undefined, statusId?: string | null): string => {
    const normalize = (value: string | null | undefined) => (value || '').toLowerCase().trim();
    const mapFallbackStatus = (raw: string): string => {
      const normalized = normalize(raw);
      if (['open', 'offen', 'açık', 'acik'].includes(normalized)) return t('pages.listen:defaults.status.open');
      if (['in progress', 'in_progress', 'in bearbeitung', 'devam ediyor'].includes(normalized)) return t('pages.listen:defaults.status.inProgress');
      if (['completed', 'erledigt', 'tamamlandı', 'tamamlandi'].includes(normalized)) return t('pages.listen:defaults.status.completed');
      if (['deferred', 'zurückgestellt', 'zuruckgestellt', 'ertelendi'].includes(normalized)) return t('pages.listen:defaults.status.deferred');
      return raw || '-';
    };

    // If statusId is provided, find the status in availableStatus
    if (statusId && availableStatus.length > 0) {
      const statusObj = availableStatus.find((s: any) => s.id === statusId);
      if (statusObj) {
        return statusObj.displayName || statusObj.name;
      }
      // If ID exists but not found in list, it was deleted - return '-'
      return '-';
    }

    // Match by translated name or base name
    if (status && availableStatus.length > 0) {
      const normalizedStatus = normalize(status);
      const statusObj = availableStatus.find((s: any) => {
        return normalize(s.name) === normalizedStatus || normalize(s.displayName) === normalizedStatus;
      });
      if (statusObj) {
        return statusObj.displayName || statusObj.name;
      }
      // If not found in list, it was deleted - return '-'
      return '-';
    }

    return status ? mapFallbackStatus(status) : '-';
  }, [availableStatus, t]);

  // Helper to get gewerk display name with validation
  const getGewerkDisplayName = useCallback((gewerk: string | null | undefined, gewerkId?: string | null): string => {
    if (!gewerk && !gewerkId) return '-';

    // If gewerkId is provided, find the gewerk in gewerke list
    if (gewerkId && gewerke.length > 0) {
      const gewerkObj = gewerke.find((g: any) => g.id === gewerkId);
      if (gewerkObj) {
        return gewerkObj.displayName || gewerkObj.name;
      }
      // If ID exists but not found in list, it was deleted - return '-'
      return '-';
    }

    // If gewerk text is provided, match against both translated and base names
    if (gewerk && gewerke.length > 0) {
      const normalized = gewerk.toLowerCase().trim();
      const gewerkObj = gewerke.find((g: any) =>
        String(g.name || '').toLowerCase().trim() === normalized ||
        String(g.displayName || '').toLowerCase().trim() === normalized
      );
      if (gewerkObj) {
        return gewerkObj.displayName || gewerkObj.name;
      }
      // If not found in list, it was deleted - return '-'
      return '-';
    }

    return gewerk || '-';
  }, [gewerke]);


  const isLoading = statsLoading || projectsLoading || (shouldLoadTickets && ticketsLoading);

  // Calculate assigned tickets for current user
  const assignedTickets = useMemo(() => {
    if (!currentUser?.id) return [];
    return allTickets.filter((t: Ticket) => (t as any).assignedContractorId === currentUser.id);
  }, [allTickets, currentUser?.id]);

  // Fetch team members for contractor name resolution - Only when tickets tabs are active
  const { data: teamResponse = [] } = useQuery({
    queryKey: ["/api/contractors", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/contractors?organizationId=${encodeURIComponent(organizationId)}` : "/api/contractors";
      const response = await apiClient.customRequest(url);
      return response;
    },
    enabled: !!organizationId && shouldLoadTickets,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });

  const teamMembers = (teamResponse as any)?.data || [];

  // Memoized helper to get contractor name from assignedContractorId
  const getContractorName = useCallback((assignedContractorId: string | null | undefined) => {
    if (!assignedContractorId) return '-';
    const contractor = teamMembers.find((m: any) => m.user_id === assignedContractorId || m.id === assignedContractorId);
    return contractor?.display_name || contractor?.email || '-';
  }, [teamMembers]);

  // Memoized helper to get localized priority display name and color
  // Uses displayName from API (database-backed translations) with fallback to name
  const getPriorityDisplayName = useCallback((rawName: string | null | undefined, priorityId?: string | null): string => {
    if (!rawName && !priorityId) return '-';

    // Try to find priority by ID first
    if (priorityId && priorities.length > 0) {
      const priority = priorities.find(p => p.id === priorityId);
      if (priority) {
        // Use displayName from API (database-backed translation) with fallback to name
        return priority.displayName || priority.name;
      }
      // If priorityId exists but priority not found (deleted), return '-'
      if (priorityId) {
        return '-';
      }
    }

    // Fallback to name matching (only if priorityId is null/undefined)
    if (rawName) {
      // If priorities list is loaded, try to find matching priority
      if (priorities.length > 0) {
        const normalizedName = rawName.toLowerCase().trim();
        const priority = priorities.find(p =>
          p.name.toLowerCase().trim() === normalizedName ||
          (p.displayName || '').toLowerCase().trim() === normalizedName
        );
        if (priority) {
          // Use displayName from API (database-backed translation) with fallback to name
          return priority.displayName || priority.name;
        }
      }

      // Legacy fallback mapping if list is not loaded yet
      const normalized = rawName.toLowerCase().trim();
      if (['low', 'niedrig', 'düşük', 'dusuk'].includes(normalized)) return t('pages.listen:defaults.priorities.low');
      if (['middle', 'medium', 'mittel', 'orta'].includes(normalized)) return t('pages.listen:defaults.priorities.medium');
      if (['high', 'hoch', 'yüksek', 'yuksek'].includes(normalized)) return t('pages.listen:defaults.priorities.high');
      if (['critical', 'kritisch', 'kritik'].includes(normalized)) return t('pages.listen:defaults.priorities.critical');

      return rawName;
    }

    return '-';
  }, [priorities, t]);

  // Memoized helper to get priority color class based on priorityId or name
  const getPriorityColorClass = useCallback((rawName: string | null | undefined, priorityId?: string | null): string => {
    // Try to find priority by ID first
    if (priorityId && priorities.length > 0) {
      const priority = priorities.find(p => p.id === priorityId);
      if (priority?.color) {
        return `bg-[${priority.color}]20 text-[${priority.color}]`;
      }
    }

    // Fallback to name matching
    if (rawName && priorities.length > 0) {
      const priority = priorities.find(p => p.name.toLowerCase() === rawName.toLowerCase());
      if (priority?.color) {
        return `bg-[${priority.color}]20 text-[${priority.color}]`;
      }
    }

    // Legacy fallback
    if (!rawName) return 'bg-blue-100 text-blue-700';
    const normalized = String(rawName).toLowerCase().trim();
    if (['hoch', 'high', 'yüksek', 'kritisch', 'critical', 'kritik'].includes(normalized)) {
      return 'bg-orange-100 text-orange-700';
    }
    if (['mittel', 'medium', 'normal', 'orta'].includes(normalized)) {
      return 'bg-yellow-100 text-yellow-700';
    }
    return 'bg-blue-100 text-blue-700';
  }, [priorities]);

  // Helper to get priority color for inline styles
  const getPriorityColor = useCallback((rawName: string | null | undefined, priorityId?: string | null): string => {
    if (priorityId && priorities.length > 0) {
      const priority = priorities.find(p => p.id === priorityId);
      if (priority?.color) return priority.color;
    }
    if (rawName && priorities.length > 0) {
      const priority = priorities.find(p => p.name.toLowerCase() === rawName.toLowerCase());
      if (priority?.color) return priority.color;
    }
    return '#6b7280'; // Default gray
  }, [priorities]);

  const getStatusBySemantic = useCallback((semanticType: string) => {
    return availableStatus.find((s: any) => {
      const semantic = String(s.semanticType || '').toLowerCase().trim().replace('-', '_');
      return semantic === semanticType;
    });
  }, [availableStatus]);

  const openStatus = useMemo(() => getStatusBySemantic('open'), [getStatusBySemantic]);
  const completedStatus = useMemo(() => getStatusBySemantic('completed'), [getStatusBySemantic]);
  const inProgressStatus = useMemo(() => getStatusBySemantic('in_progress'), [getStatusBySemantic]);
  const deferredStatus = useMemo(() => getStatusBySemantic('deferred'), [getStatusBySemantic]);

  const openStatusLabel = openStatus?.displayName || openStatus?.name || 'Open';
  const completedStatusLabel = completedStatus?.displayName || completedStatus?.name || 'Completed';
  const inProgressStatusLabel = inProgressStatus?.displayName || inProgressStatus?.name || 'In Progress';
  const deferredStatusLabel = deferredStatus?.displayName || deferredStatus?.name || 'Deferred';
  const openStatusColor = openStatus?.color || getStatusColor(openStatusLabel, availableStatus);

  const getProjectStatusDisplay = useCallback((statusId?: string | null) => {
    if (statusId) {
      const status = projectStatuses.find((s) => s.id === statusId);
      if (status) {
        let label = status.displayName || status.name;
        const translationKey = getProjectCardStatusTranslationKey(status.semanticType);
        if (translationKey) {
          label = t(`components.projectCard:status.${translationKey}`, { defaultValue: label });
        }
        return {
          label,
          color: status.color || '#6b7280',
        };
      }
    }
    return {
      label: t('pages.dashboard:values.notAvailable'),
      color: '#6b7280',
    };
  }, [projectStatuses, t]);

  // Calculate filtered ticket arrays for overdue and due soon
  const overdueTickets = useMemo(() => {
    return allTickets.filter((t: Ticket) =>
      t.dueDate &&
      new Date(t.dueDate) < new Date() &&
      (t as any).statusSemanticType !== 'completed'
    );
  }, [allTickets]);

  const dueSoonTickets = useMemo(() => {
    return allTickets.filter((t: Ticket) =>
      t.dueDate &&
      new Date(t.dueDate) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) &&
      new Date(t.dueDate) > new Date()
    );
  }, [allTickets]);

  // Calculate overtime tickets
  const overtimeTickets = useMemo(() => {
    if (!shouldLoadTickets || !allTickets.length) return [];
    return allTickets.filter((t: Ticket) => (t as any).overtimeNeeded === true);
  }, [shouldLoadTickets, allTickets]);

  // Memoize expensive calculations for tabs - Use stats API when tickets not loaded
  const tabStats = useMemo(() => {
    if (shouldLoadTickets && ticketsForChipCounts.length > 0) {
      // Use actual ticket data when loaded
      return {
        myTickets: ticketsForChipCounts.length,
        assignedTickets: ticketsForChipCounts.filter((t: Ticket) => (t as any).assignedContractorId === currentUser?.id).length,
        overdueTickets: ticketsForChipCounts.filter((t: Ticket) =>
          t.dueDate &&
          new Date(t.dueDate) < new Date() &&
          (t as any).statusSemanticType !== 'completed'
        ).length,
        dueSoonTickets: ticketsForChipCounts.filter((t: Ticket) =>
          t.dueDate &&
          new Date(t.dueDate) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) &&
          new Date(t.dueDate) > new Date()
        ).length,
        overtimeTickets: ticketsForChipCounts.filter((t: Ticket) => (t as any).overtimeNeeded === true).length,
      };
    } else {
      // Use stats API data when tickets not loaded yet
      return {
        myTickets: (stats.openTickets || 0) + (stats.inProgressTickets || 0) + (stats.completedTickets || 0),
        assignedTickets: 0, // Will be calculated when tickets load
        overdueTickets: 0, // Will be calculated when tickets load
        dueSoonTickets: 0, // Will be calculated when tickets load
        overtimeTickets: 0, // Will be calculated when tickets load
      };
    }
  }, [shouldLoadTickets, ticketsForChipCounts, currentUser?.id, stats]);

  const statusStats = useMemo(() => {
    return availableStatus.map((status: any) => ({
      id: status.id,
      name: status.displayName || status.name,
      color: status.color || '#6b7280',
      count: ticketsForChipCounts.filter((ticket: Ticket) => (ticket as any).statusId === status.id).length,
    }));
  }, [availableStatus, ticketsForChipCounts]);


  const handleProjectClick = (project: Project) => {
    setLocation(`/project/${project.id}`);
  };

  // Check if user can view ticket
  const canViewTicket = (ticket: Ticket): boolean => {
    // Contractors can ONLY view tickets assigned to them
    if (isContractor) {
      return (ticket as any).assignedContractorId === currentUser?.id;
    }
    // For non-contractors: can view if assigned OR has project access permission
    const isAssigned = (ticket as any).assignedContractorId === currentUser?.id;
    const hasManagePermission =
      hasPermission('manage_projects') ||
      hasPermission('manage_all_projects') ||
      hasPermission('view_projects') ||
      hasPermission('view_all_projects');
    return isAssigned || hasManagePermission;
  };

  // Handle ticket row click
  const handleTicketClick = (ticket: Ticket) => {
    if (canViewTicket(ticket)) {
      setSelectedTicket(ticket);
      setIsTicketModalOpen(true);
    } else {
      setShowAccessDeniedDialog(true);
    }
  };

  // Handle ticket modal close
  const handleTicketModalClose = () => {
    setIsTicketModalOpen(false);
    setSelectedTicket(null);
  };

  // Handle ticket updated
  const handleTicketUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/tickets", organizationId] });
    queryClient.invalidateQueries({ queryKey: ["/api/stats", organizationId] });
  };

  // Berechne Ticket-Statistiken pro Projekt - MEMOIZED
  const getProjectTicketStats = useCallback((projectId: string): ProjectTicketStats => {
    const projectTickets = ticketsForChipCounts.filter((ticket: Ticket) => ticket.projectId === projectId);
    const now = new Date();

    // Dynamisch alle Status aus verfügbaren Status ermitteln (using statusId for matching)
    const statusCounts: Record<string, number> = {};
    availableStatus.forEach((status: any) => {
      statusCounts[status.id] = projectTickets.filter((t: Ticket) => (t as any).statusId === status.id).length;
    });

    // Like-Statistiken berechnen
    const totalLikes = projectTickets.reduce((sum: number, ticket: Ticket) => {
      const likes = Number((ticket as any).likes);
      return sum + (Number.isFinite(likes) && likes > 0 ? likes : 0);
    }, 0);
    const likedTickets = projectTickets.filter((t: Ticket) => {
      const likes = Number((t as any).likes);
      return Number.isFinite(likes) && likes > 0;
    }).length;

    const stats = {
      projectId,
      total: projectTickets.length,
      overdue: projectTickets.filter((t: Ticket) =>
        t.dueDate && new Date(t.dueDate) < now && (t as any).statusSemanticType !== 'completed'
      ).length,
      statusCounts,
      totalLikes, // Gesamt-Likes für alle Tickets
      likedTickets, // Anzahl Tickets mit mindestens 1 Like
      assignedToMe: projectTickets.filter((t: Ticket) => (t as any).assignedContractorId === currentUser?.id).length,
      overtime: projectTickets.filter((t: Ticket) => (t as any).overtimeNeeded === true).length,
    };

    return stats;
  }, [ticketsForChipCounts, availableStatus, currentUser?.id]);

  // Status-Farb-Mapping aus Datenbank - REMOVED UNUSED FUNCTION
  // const getStatusColor = (statusName: string): string => {
  //   const status = availableStatus.find((s: any) => s.name === statusName);
  //   return status?.color || '#6b7280'; // Fallback grau
  // };

  // Erstelle Kreisdiagramm-Daten für ein Projekt - MEMOIZED
  const createChartData = useCallback((stats: ProjectTicketStats) => {
    return availableStatus
      .map((status: any) => ({
        name: status.displayName || status.name,
        value: stats.statusCounts[status.id] || 0,
        color: status.color
      }))
      .filter((segment: any) => segment.value > 0);
  }, [availableStatus]);

  // PDF-Dokument für Projektzusammenfassung
  const ProjectSummaryPDF = ({ project, ticketStats, completedStatusId }: { project: Project; ticketStats: ProjectTicketStats; completedStatusId: string | null }) => (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.header}>{t('pages.dashboard:pdf.header')}</Text>

        <Text style={pdfStyles.projectTitle}>{project.name}</Text>

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>{t('pages.dashboard:pdf.sections.projectInfo')}</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.projectNumber')}</Text>
            <Text style={pdfStyles.value}>{project.projektNummer || t('pages.dashboard:values.notAvailable')}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.projectStart')}</Text>
            <Text style={pdfStyles.value}>
              {project.startDatum ? formatDate(project.startDatum) : t('pages.dashboard:values.notAvailable')}
            </Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.projectEnd')}</Text>
            <Text style={pdfStyles.value}>
              {project.endDatum ? formatDate(project.endDatum) : t('pages.dashboard:values.notAvailable')}
            </Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.manager')}</Text>
            <Text style={pdfStyles.value}>{project.managerName || t('pages.dashboard:values.notAssigned')}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.priority')}</Text>
            <Text style={pdfStyles.value}>{project.prioritaet || t('pages.dashboard:values.notSet')}</Text>
          </View>
        </View>

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>{t('pages.dashboard:pdf.sections.ticketStats')}</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.totalTickets')}</Text>
            <Text style={pdfStyles.value}>{ticketStats.total}</Text>
          </View>
          <View style={pdfStyles.statusGrid}>
            {availableStatus.map((status: any) => (
              <View key={status.id} style={pdfStyles.statusItem}>
                <Text style={[pdfStyles.label, { color: status.color }]}>{status.displayName || status.name}:</Text>
                <Text style={pdfStyles.value}>{ticketStats.statusCounts[status.id] || 0}</Text>
              </View>
            ))}
          </View>
        </View>

        {ticketStats.overdue > 0 && (
          <View style={pdfStyles.section}>
            <Text style={[pdfStyles.sectionTitle, { color: '#dc2626' }]}>{t('pages.dashboard:pdf.sections.critical')}</Text>
            <View style={pdfStyles.row}>
              <Text style={[pdfStyles.label, { color: '#dc2626' }]}>{t('pages.dashboard:pdf.labels.overdueTickets')}</Text>
              <Text style={[pdfStyles.value, { color: '#dc2626' }]}>{ticketStats.overdue}</Text>
            </View>
          </View>
        )}

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>{t('pages.dashboard:pdf.sections.progress')}</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.completion')}</Text>
            <Text style={pdfStyles.value}>
              {ticketStats.total > 0 ?
                `${Math.round((((completedStatusId ? ticketStats.statusCounts[completedStatusId] : 0) || 0) / ticketStats.total) * 100)}%`
                : '0%'
              }
            </Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>{t('pages.dashboard:pdf.labels.openWork')}</Text>
              <Text style={pdfStyles.value}>
                {ticketStats.total > 0 ?
                `${Math.round(((ticketStats.total - ((completedStatusId ? ticketStats.statusCounts[completedStatusId] : 0) || 0)) / ticketStats.total) * 100)}%`
                : '0%'
              }
            </Text>
          </View>
        </View>

        <Text style={pdfStyles.footer}>
          {t('pages.dashboard:pdf.footer', { date: formatDate(new Date()), time: new Date().toLocaleTimeString(locale) })}
        </Text>
      </Page>
    </Document>
  );

  // Project cards memoized - Original dashboard card structure
  const projectCards = useMemo(() => {
    return filteredProjects.map((project: Project) => {
      const ticketStats = getProjectTicketStats(project.id);
      const chartData = createChartData(ticketStats);
      const statusItems = [
        {
          key: 'open',
          label: openStatusLabel,
          count: openStatus?.id ? (ticketStats.statusCounts[openStatus.id] || 0) : 0,
          color: openStatus?.color || openStatusColor,
        },
        {
          key: 'overtime',
          label: t('pages.dashboard:labels.overtimeShort'),
          count: ticketStats.overtime,
          color: overtimeTabColor,
        },
        {
          key: 'inProgress',
          label: inProgressStatusLabel,
          count: inProgressStatus?.id ? (ticketStats.statusCounts[inProgressStatus.id] || 0) : 0,
          color: inProgressStatus?.color || getStatusColor(inProgressStatusLabel, availableStatus),
        },
        {
          key: 'postponed',
          label: deferredStatusLabel,
          count: deferredStatus?.id ? (ticketStats.statusCounts[deferredStatus.id] || 0) : 0,
          color: deferredStatus?.color || getStatusColor(deferredStatusLabel, availableStatus),
        },
        {
          key: 'assigned',
          label: t('pages.dashboard:labels.assignedToMeShort'),
          count: ticketStats.assignedToMe,
          color: assignedTabColor,
        },
        {
          key: 'completed',
          label: completedStatusLabel,
          count: completedStatus?.id ? (ticketStats.statusCounts[completedStatus.id] || 0) : 0,
          color: completedStatus?.color || getStatusColor(completedStatusLabel, availableStatus),
        },
      ];

      // Get project lead and position
      const managerName = (project as any).managerName || '';
      const teamMembers = (project as any).teamMitglieder || [];
      const projectLead = teamMembers.find((m: any) => m.name === managerName) || teamMembers[0] || null;
      const position = projectLead?.position || '';
      const completedCount = completedStatus?.id ? (ticketStats.statusCounts[completedStatus.id] || 0) : 0;
      const progress = (project as any).fortschritt || (ticketStats.total > 0 ? Math.round((completedCount / ticketStats.total) * 100) : 0);
      const projectStatusDisplay = getProjectStatusDisplay((project as any).projectStatusId);
      const projectImageUrl = project.bildUrl;

      return (
        <Card
          key={project.id}
          className="card-container card-container-muted card-container-no-shadow overflow-hidden max-w-[300px] w-full aspect-[660/1115] flex flex-col"
        >
          <div className="relative w-full flex-[0_0_50%] overflow-hidden">
            {projectImageUrl ? (
              <img
                src={projectImageUrl}
                alt={project.name}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = "/images/logos/logo_outline.png";
                  event.currentTarget.className = "h-16 w-16 object-contain";
                  if (event.currentTarget.parentElement) {
                    event.currentTarget.parentElement.classList.add(
                      "bg-[#DDDDDD]",
                      "flex",
                      "items-center",
                      "justify-center"
                    );
                  }
                }}
              />
            ) : (
              <div className="h-full w-full bg-[#DDDDDD] flex items-center justify-center">
                <img src="/images/logos/logo_outline.png" alt="" className="h-16 w-16 object-contain" aria-hidden="true" />
              </div>
            )}
          </div>
          <CardContent className="p-4 flex flex-col gap-3 bg-transparent flex-[0_0_50%]">
            <div>
              <h3 className="text-base font-bold text-[#333333] leading-tight truncate">
                {project.name}
              </h3>
              <div className="text-xs text-slate-600 mt-1 line-clamp-2">
                {project.beschreibung || project.projektNummer || t('pages.dashboard:values.notAvailable')}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {statusItems.map((status) => {
                const statusColor = status.color || projectsTabColor;
                return (
                  <div
                    key={status.key}
                    className="rounded-xl px-2.5 py-2 text-center"
                    style={{
                      backgroundColor: statusColor,
                      border: `1px solid ${statusColor}`
                    }}
                  >
                    <div className="text-lg font-semibold text-black">
                      {status.count}
                    </div>
                    <div className="text-xs font-medium text-black truncate">
                      {status.label}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-center">
              <span
                className="px-4 py-1 rounded-full text-xs font-semibold min-w-[120px] text-center"
                style={{
                  backgroundColor: projectStatusDisplay.color,
                  color: '#ffffff',
                }}
              >
                {projectStatusDisplay.label}
              </span>
            </div>
          </CardContent>
        </Card>
      );
    });
  }, [filteredProjects, getProjectTicketStats, createChartData, t, locale, formatDate, availableStatus, openStatus, openStatusLabel, openStatusColor, inProgressStatus, inProgressStatusLabel, deferredStatus, deferredStatusLabel, completedStatus, completedStatusLabel, orgLogoUrl, getProjectStatusDisplay]);

  // PDF-Styles für A4-Projektzusammenfassung
  const pdfStyles = StyleSheet.create({
    page: {
      flexDirection: 'column',
      backgroundColor: '#FFFFFF',
      padding: 30,
      fontSize: 10,
      fontFamily: 'Helvetica',
    },
    header: {
      fontSize: 16,
      marginBottom: 20,
      textAlign: 'center',
      color: '#1e293b',
      fontFamily: 'Helvetica-Bold',
    },
    projectTitle: {
      fontSize: 14,
      marginBottom: 15,
      color: '#334155',
      fontFamily: 'Helvetica-Bold',
    },
    section: {
      marginBottom: 15,
    },
    sectionTitle: {
      fontSize: 12,
      marginBottom: 8,
      color: '#475569',
      fontFamily: 'Helvetica-Bold',
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 2,
    },
    label: {
      flex: 1,
      fontSize: 9,
      color: '#64748b',
    },
    value: {
      flex: 1,
      fontSize: 9,
      color: '#1e293b',
      fontFamily: 'Helvetica-Bold',
      textAlign: 'right',
    },
    statusGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    statusItem: {
      width: '48%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 3,
      marginBottom: 2,
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
      right: 30,
      textAlign: 'center',
      fontSize: 8,
      color: '#64748b',
    }
  });


  if (isLoading && !stats && projects.length === 0) {
    return (
      <AppShell>
        {/* Header Skeleton */}
        <header className="page-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-6 w-32 bg-slate-200 rounded animate-pulse"></div>
              <div className="h-8 w-40 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="w-8 h-8 bg-slate-200 rounded-full animate-pulse"></div>
          </div>
        </header>

        {/* Content Skeleton */}
        <main className="flex-1 overflow-auto bg-white">
          <div className="flex flex-col lg:flex-row gap-4 h-full p-4">
            <div className="flex-1 min-w-0">
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 400px))', justifyContent: 'start' }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card-project p-5">
                    <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-4"></div>
                    <div className="space-y-2 mb-4">
                      <div className="h-4 w-full bg-slate-200 rounded animate-pulse"></div>
                      <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
                      <div className="h-4 w-1/2 bg-slate-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-2 w-full bg-slate-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-[240px] xl:w-[260px] 2xl:w-[280px]">
              <div className="flex flex-col gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card-container px-4 py-3 flex items-center justify-between">
                    <div className="h-5 w-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="h-3 w-20 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </AppShell>
    );
  }

  // Block contractors and users without any project read permission from dashboard data.
  if (isContractor || (!permissionsLoading && !canAccessDashboardProjectData)) {
    return (
      <AppShell>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-bold text-slate-900 uppercase tracking-wide">{t('pages.dashboard:title')}</h1>
            </div>
            <div className="flex items-center space-x-3">
              <OrganizationAvatar />
            </div>
          </div>
        </header>
        {/* Main Content */}
        <main className="flex-1 overflow-auto px-6 pt-0 pb-6 bg-slate-50">
          <AccessDenied />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageHeaderLayout
        pageTitle={t('pages.dashboard:title')}
        pageSubtitle={orgInfo?.name || undefined}
        searchComponent={
          <HeaderSearchInput
            value={projectSearchTerm}
            onChange={setProjectSearchTerm}
            placeholder={t('pages.projects:searchPlaceholder')}
          />
        }
      >
        {/* Main Content mit Tab-System */}
        <div className="flex-1 overflow-auto bg-white">
          <Tabs
            value={activeTab}
            onValueChange={(nextTab) => {
              if (nextTab === 'meine-tickets' && !canInteractDashboardTicketSummary) {
                return;
              }
              setActiveTab(nextTab);
              if (nextTab !== 'meine-tickets') {
                setSelectedStatusFilterId(null);
              }
            }}
            className="h-full"
          >
            <div className="flex flex-col lg:flex-row gap-4 h-full">
              <div className="w-full lg:w-[240px] xl:w-[260px] 2xl:w-[280px] p-4 pt-0 order-1 lg:order-2 h-full">
                <div className="h-full overflow-y-auto pr-1">
                <TabsList className="flex flex-col gap-3 bg-transparent p-0 h-auto">
                  <TabsTrigger
                    value="projekte"
                    className="card-container card-container-no-shadow w-full px-4 py-4 text-left flex items-center justify-between"
                    style={{
                      backgroundColor: projectsTabColor,
                      border: `1px solid ${projectsTabColor}`,
                      color: '#111827'
                    }}
                  >
                    <div className="grid grid-cols-[1fr_2fr] items-center w-full">
                      <span className="text-2xl font-semibold text-right pr-2 truncate">{filteredProjects.length}</span>
                      <span className="text-xs font-normal uppercase tracking-wide text-left pl-2 truncate">
                        {t('pages.dashboard:tabs.projects')}
                      </span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="meine-tickets"
                    className="card-container card-container-no-shadow w-full px-4 py-4 text-left flex items-center justify-between"
                    disabled={!canInteractDashboardTicketSummary}
                    onClick={() => {
                      if (!canInteractDashboardTicketSummary) return;
                      setSelectedStatusFilterId(null);
                    }}
                    title={!canInteractDashboardTicketSummary ? t('common:accessDenied.message') : undefined}
                    style={{
                      backgroundColor: openStatusColor,
                      border: `1px solid ${openStatusColor}`,
                      color: '#111827',
                      cursor: canInteractDashboardTicketSummary ? 'pointer' : 'not-allowed',
                      opacity: canInteractDashboardTicketSummary ? 1 : 0.65,
                    }}
                  >
                    <div className="grid grid-cols-[1fr_2fr] items-center w-full">
                      <span className="text-2xl font-semibold text-right pr-2 truncate">{tabStats.myTickets}</span>
                      <span className="text-xs font-normal uppercase tracking-wide text-left pl-2 truncate">
                        {t('pages.dashboard:tabs.allTickets')}
                      </span>
                    </div>
                  </TabsTrigger>
                </TabsList>
                <div className="mt-3 flex flex-col gap-3">
                  {statusStats.map((status) => (
                    <button
                      type="button"
                      key={status.id}
                      className="card-container card-container-no-shadow w-full px-4 py-4 text-left flex items-center justify-between"
                      disabled={!canInteractDashboardTicketSummary}
                      onClick={() => {
                        if (!canInteractDashboardTicketSummary) return;
                        setSelectedStatusFilterId(prev => prev === status.id ? null : status.id);
                        setActiveTab('meine-tickets');
                      }}
                      title={!canInteractDashboardTicketSummary ? t('common:accessDenied.message') : undefined}
                      style={{
                        backgroundColor: status.color,
                        border: selectedStatusFilterId === status.id ? '2px solid #111827' : `1px solid ${status.color}`,
                        color: '#111827',
                        cursor: canInteractDashboardTicketSummary ? 'pointer' : 'not-allowed',
                        opacity: canInteractDashboardTicketSummary ? 1 : 0.65,
                      }}
                    >
                      <div className="grid grid-cols-[1fr_2fr] items-center w-full">
                        <span className="text-2xl font-semibold text-right pr-2 truncate">{status.count}</span>
                        <span className="text-xs font-normal uppercase tracking-wide text-left pl-2 truncate">
                          {status.name}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
                </div>
              </div>
              <div className="flex-1 min-w-0 p-4 pt-0 order-2 lg:order-1">
                {/* Tab-Inhalte */}
                <TabsContent value="projekte" className="m-0 h-full overflow-auto">
                  {/* Project Cards Grid */}
                  {projectsLoading ? (
                    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="card-project p-5">
                          <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-4"></div>
                          <div className="space-y-2 mb-4">
                            <div className="h-4 w-full bg-slate-200 rounded animate-pulse"></div>
                            <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
                            <div className="h-4 w-1/2 bg-slate-200 rounded animate-pulse"></div>
                          </div>
                          <div className="h-2 w-full bg-slate-200 rounded animate-pulse mb-4"></div>
                          <div className="h-12 w-12 bg-slate-200 rounded-full animate-pulse mx-auto"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 300px))', justifyContent: 'start' }}>
                      {projectCards}
                    </div>
                  )}
                </TabsContent>

                {/* Meine Tickets Tab */}
                <TabsContent value="meine-tickets" className="m-0 h-full overflow-auto">
                  {shouldLoadTickets && ticketsLoading ? (
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-300">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Title</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Priority</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Project</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...Array(8)].map((_, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="px-3 py-3"><div className="h-4 w-12 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-48 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-20 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-16 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <TicketsTable tickets={filteredTicketsByStatus} projects={projects} teamMembers={teamMembers} onTicketClick={handleTicketClick} isContractor={isContractor} currentUser={currentUser} getPriorityDisplayName={getPriorityDisplayName} getPriorityColorClass={getPriorityColorClass} getPriorityColor={getPriorityColor} availableStatus={availableStatus} formatDate={formatDate} getStatusDisplayName={getStatusDisplayName} getGewerkDisplayName={getGewerkDisplayName} />
                  )}
                </TabsContent>

                {/* Zugewiesene Tickets Tab */}
                <TabsContent value="zugewiesene-tickets" className="m-0 h-full overflow-auto">
                  <TicketsTable tickets={assignedTickets} projects={projects} teamMembers={teamMembers} onTicketClick={handleTicketClick} isContractor={isContractor} currentUser={currentUser} getPriorityDisplayName={getPriorityDisplayName} getPriorityColorClass={getPriorityColorClass} getPriorityColor={getPriorityColor} checkContractorAccess={true} availableStatus={availableStatus} formatDate={formatDate} getStatusDisplayName={getStatusDisplayName} getGewerkDisplayName={getGewerkDisplayName} />
                </TabsContent>

                {/* Overtime Tickets Tab */}
                <TabsContent value="overtime-tickets" className="m-0 h-full overflow-auto">
                  {shouldLoadTickets && ticketsLoading ? (
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-300">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Title</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Priority</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Project</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...Array(8)].map((_, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="px-3 py-3"><div className="h-4 w-12 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-48 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-20 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-16 bg-slate-200 rounded animate-pulse"></div></td>
                                <td className="px-3 py-3"><div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <>
                      <TicketsTable tickets={overtimeTickets} projects={projects} teamMembers={teamMembers} onTicketClick={handleTicketClick} isContractor={isContractor} currentUser={currentUser} getPriorityDisplayName={getPriorityDisplayName} getPriorityColorClass={getPriorityColorClass} getPriorityColor={getPriorityColor} checkContractorAccess={true} availableStatus={availableStatus} formatDate={formatDate} getStatusDisplayName={getStatusDisplayName} getGewerkDisplayName={getGewerkDisplayName} />
                      {overtimeTickets.length > 0 && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="text-sm font-semibold text-slate-700">
                            {t('pages.dashboard:overtime.total', {
                              hours: overtimeTickets.reduce((total, ticket) => {
                                const hours = (ticket as any).overtimeHours || 0;
                                return total + hours;
                              }, 0)
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Überfällige Tickets Tab */}
                <TabsContent value="ueberfaellige-tickets" className="m-0 h-full overflow-auto">
                  <TicketsTable tickets={overdueTickets} projects={projects} teamMembers={teamMembers} onTicketClick={handleTicketClick} isContractor={isContractor} currentUser={currentUser} getPriorityDisplayName={getPriorityDisplayName} getPriorityColorClass={getPriorityColorClass} getPriorityColor={getPriorityColor} checkContractorAccess={true} availableStatus={availableStatus} formatDate={formatDate} getStatusDisplayName={getStatusDisplayName} getGewerkDisplayName={getGewerkDisplayName} />
                </TabsContent>

                {/* Bald fällige Tickets Tab */}
                <TabsContent value="bald-faellige-tickets" className="m-0 h-full overflow-auto">
                  <TicketsTable tickets={dueSoonTickets} projects={projects} teamMembers={teamMembers} onTicketClick={handleTicketClick} isContractor={isContractor} currentUser={currentUser} getPriorityDisplayName={getPriorityDisplayName} getPriorityColorClass={getPriorityColorClass} getPriorityColor={getPriorityColor} checkContractorAccess={true} availableStatus={availableStatus} formatDate={formatDate} getStatusDisplayName={getStatusDisplayName} getGewerkDisplayName={getGewerkDisplayName} />
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </div>
      </PageHeaderLayout>

      {/* Ticket Modal */}
      {selectedTicket && (
        <EditTicketInlineModal
          isOpen={isTicketModalOpen}
          onClose={handleTicketModalClose}
          ticket={selectedTicket}
          projectId={selectedTicket.projectId}
          planLocation={(selectedTicket as any).planLocation}
          prefetchedStatus={availableStatus}
          prefetchedPriorities={priorities}
          prefetchedGewerke={gewerke}
          prefetchedTypen={typen}
          onUpdated={handleTicketUpdated}
        />
      )}

      {/* Access Denied Dialog */}
      <Dialog open={showAccessDeniedDialog} onOpenChange={setShowAccessDeniedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pages.dashboard:errors.accessDenied')}</DialogTitle>
            <DialogDescription>
              {t('pages.dashboard:errors.ticketViewNotAllowed')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowAccessDeniedDialog(false)}>
              {t('pages.dashboard:deleteDialog.cancel')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </AppShell>
  );
}

// Unified Tickets Table Component - Reusable for all ticket lists
function TicketsTable({ tickets, projects, teamMembers, onTicketClick, isContractor, currentUser, getPriorityDisplayName, getPriorityColorClass, getPriorityColor, checkContractorAccess = false, availableStatus = [], formatDate, getStatusDisplayName, getGewerkDisplayName }: {
  tickets: Ticket[];
  projects: Project[];
  teamMembers?: any[];
  onTicketClick?: (ticket: Ticket) => void;
  isContractor?: boolean;
  currentUser?: any;
  getPriorityDisplayName?: (rawName: string | null | undefined, priorityId?: string | null) => string;
  getPriorityColorClass?: (rawName: string | null | undefined, priorityId?: string | null) => string;
  getPriorityColor?: (rawName: string | null | undefined, priorityId?: string | null) => string;
  checkContractorAccess?: boolean; // If true, contractors can only click tickets assigned to them
  availableStatus?: any[]; // Status list for color lookup
  formatDate: (date: Date | string | null | undefined) => string;
  getStatusDisplayName?: (status: string | null | undefined, statusId?: string | null) => string;
  getGewerkDisplayName?: (gewerk: string | null | undefined, gewerkId?: string | null) => string;
}) {
  const { t, i18n } = useTranslation(['pages.dashboard']);
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const [ticketSortBy, setTicketSortBy] = useState<string>("ticketNumber");

  // Create a map of project currencies for efficient lookup
  const projectCurrencyMap = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach(project => {
      map.set(project.id, project.currency || 'EUR');
    });
    return map;
  }, [projects]);

  // Helper function to get currency for a ticket
  const getTicketCurrency = (ticket: Ticket) => {
    const project = projects.find(p => p.id === ticket.projectId);
    return project?.currency || 'EUR';
  };
  const [ticketSortOrder, setTicketSortOrder] = useState<"asc" | "desc">("asc");

  // Handle column header click for sorting
  const handleTicketSort = (column: string) => {
    if (ticketSortBy === column) {
      setTicketSortOrder(ticketSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setTicketSortBy(column);
      setTicketSortOrder('asc');
    }
  };

  // Sort tickets logic
  const sortedTickets = [...tickets].sort((a, b) => {
    let aValue: any, bValue: any;

    switch (ticketSortBy) {
      case 'ticketNumber':
        aValue = a.ticketNumber || '';
        bValue = b.ticketNumber || '';
        break;
      case 'title':
        aValue = a.title || '';
        bValue = b.title || '';
        break;
      case 'status':
        aValue = (a as { status?: string }).status || '';
        bValue = (b as { status?: string }).status || '';
        break;
      case 'priority':
        const priorityOrder = { 'Kritisch': 4, 'critical': 4, 'Hoch': 3, 'high': 3, 'Mittel': 2, 'medium': 2, 'Niedrig': 1, 'low': 1 };
        aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
        break;
      case 'gewerk':
        aValue = a.gewerk || '';
        bValue = b.gewerk || '';
        break;
      case 'assignee':
        aValue = (a as any).assignedContractorId || '';
        bValue = (b as any).assignedContractorId || '';
        break;
      case 'dueDate':
        aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        break;
      case 'costs':
        aValue = a.costs ? parseFloat(a.costs) : 0;
        bValue = b.costs ? parseFloat(b.costs) : 0;
        break;
      case 'progress':
        aValue = a.progress || 0;
        bValue = b.progress || 0;
        break;
      default:
        aValue = '';
        bValue = '';
    }

    if (aValue < bValue) return ticketSortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return ticketSortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Get project name for a ticket
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || t('pages.dashboard:table.unknownProject');
  };

  // Helper to get contractor name from assignedContractorId
  const getContractorName = (assignedContractorId: string | null | undefined) => {
    if (!assignedContractorId || !teamMembers) return '-';
    const contractor = teamMembers.find((m: any) => m.user_id === assignedContractorId || m.id === assignedContractorId);
    return contractor?.display_name || contractor?.email || '-';
  };

  const handleConfirmTicketDelete = (ticketId: string, ticketNumber: string, title: string) => {
    // For now, just log - in a real implementation this would delete the ticket
    console.log('Delete ticket:', ticketId, ticketNumber, title);
  };

  const renderSortableHeader = (label: string, field: string, className = '') => (
    <th
      className={`px-3 py-3 text-left cursor-pointer hover:bg-slate-50 whitespace-nowrap overflow-hidden text-ellipsis ${className}`}
      onClick={() => handleTicketSort(field)}
      style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}
    >
      <div className="flex items-center space-x-1">
        <span className="truncate">{label}</span>
        {ticketSortBy === field ? (
          ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3 flex-shrink-0" /> : <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : null}
      </div>
    </th>
  );

  return (
    <div className="w-full">
      <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none">
        <div className="overflow-auto min-h-0 min-w-0">
          <table className="min-w-[1820px] table-fixed">
            <colgroup>
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
            </colgroup>
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-slate-200">
                {renderSortableHeader(t('pages.dashboard:table.headers.nr'), 'ticketNumber')}
                <th className="px-3 py-3 text-left whitespace-nowrap overflow-hidden text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}>
                  <span className="block truncate">{t('pages.dashboard:table.headers.project')}</span>
                </th>
                {renderSortableHeader(t('pages.dashboard:table.headers.title'), 'title', 'w-[140px] min-w-[140px] max-w-[140px]')}
                {renderSortableHeader(t('pages.dashboard:table.headers.status'), 'status', 'w-[140px] min-w-[140px] max-w-[140px]')}
                {renderSortableHeader(t('pages.dashboard:table.headers.priority'), 'priority', 'w-[140px] min-w-[140px] max-w-[140px]')}
                {renderSortableHeader(t('pages.dashboard:table.headers.trade'), 'gewerk', 'w-[140px] min-w-[140px] max-w-[140px]')}
                <th className="px-3 py-3 text-left whitespace-nowrap overflow-hidden text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}>
                  <span className="block truncate">{t('pages.dashboard:table.headers.location')}</span>
                </th>
                {renderSortableHeader(t('pages.dashboard:table.headers.assignee'), 'assignee', 'w-[140px] min-w-[140px] max-w-[140px]')}
                {renderSortableHeader(t('pages.dashboard:table.headers.due'), 'dueDate', 'w-[140px] min-w-[140px] max-w-[140px]')}
                {renderSortableHeader(t('pages.dashboard:table.headers.costs'), 'costs', 'w-[140px] min-w-[140px] max-w-[140px]')}
                <th className="px-3 py-3 text-left whitespace-nowrap overflow-hidden text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}>
                  <div className="flex items-center space-x-1">
                    <Heart className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{t('pages.dashboard:table.headers.likes')}</span>
                  </div>
                </th>
                <th className="px-3 py-3 text-left whitespace-nowrap overflow-hidden text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}>
                  <span className="block truncate">{t('pages.dashboard:table.headers.overtime')}</span>
                </th>
                <th className="px-3 py-3 text-left whitespace-nowrap overflow-hidden text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}>
                  <span className="block truncate">{t('pages.dashboard:table.headers.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedTickets.map((ticket) => {
                // Check if contractor can view this ticket (only if checkContractorAccess is enabled)
                const canClick = ticket && onTicketClick && (
                  checkContractorAccess && isContractor
                    ? (ticket as any).assignedContractorId === currentUser?.id
                    : true
                );
                return (
                  <tr
                    key={ticket.id}
                    className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${canClick ? "cursor-pointer" : ""}`}
                    onClick={canClick ? () => onTicketClick!(ticket) : undefined}
                    style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
                  >
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={`#${ticket.ticketNumber || '-'}`}>
                        #{ticket.ticketNumber}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={getProjectName(ticket.projectId)}>
                        {getProjectName(ticket.projectId)}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={ticket.title}>
                        {ticket.title}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      {(() => {
                        const statusDisplayName = getStatusDisplayName
                          ? getStatusDisplayName((ticket as { status?: string }).status || '', (ticket as any).statusId)
                          : ((ticket as { status?: string }).status || '-');
                        return (
                          <span
                            className="block truncate"
                            title={statusDisplayName || '-'}
                          >
                            {statusDisplayName || '-'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      {(() => {
                        const priorityId = (ticket as any).priorityId;
                        const displayName = getPriorityDisplayName
                          ? getPriorityDisplayName(ticket.priority, priorityId)
                          : (ticket.priority || '-');
                        return (
                          <span
                            className="block truncate"
                            title={displayName}
                          >
                            {displayName}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={(getGewerkDisplayName ? getGewerkDisplayName(ticket.gewerk, (ticket as any).gewerkId) : (ticket.gewerk || '-')) || ""}>
                        {getGewerkDisplayName ? getGewerkDisplayName(ticket.gewerk, (ticket as any).gewerkId) : (ticket.gewerk || '-')}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={ticket.location || ""}>
                        {ticket.location || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={getContractorName((ticket as any).assignedContractorId)}>
                        {getContractorName((ticket as any).assignedContractorId)}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={formatDate(ticket.dueDate)}>
                        {formatDate(ticket.dueDate)}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <span className="block truncate" title={ticket.costs ? new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: getTicketCurrency(ticket),
                        minimumFractionDigits: 0
                      }).format(parseFloat(ticket.costs)) : '-'}>
                        {ticket.costs ? new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency: getTicketCurrency(ticket),
                          minimumFractionDigits: 0
                        }).format(parseFloat(ticket.costs)) : '-'}
                      </span>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <div className="flex items-center justify-center gap-2">
                        <Heart className={`h-3 w-3 ${ticket.likes && ticket.likes > 0 ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} />
                        <span>{ticket.likes || 0}</span>
                      </div>
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      {(() => {
                        const overtimeNeeded = (ticket as any).overtimeNeeded;
                        const overtimeHours = (ticket as any).overtimeHours;
                        const overtimeConfirmed = (ticket as any).overtimeConfirmed;

                        if (!overtimeNeeded) {
                          return (
                            <span className="block truncate text-xs text-slate-500" title={t('components.ticket:overtime.notNeeded')}>
                              {t('components.ticket:overtime.notNeeded')}
                            </span>
                          );
                        }

                        if (overtimeConfirmed) {
                          const text = `${overtimeHours} ${overtimeHours === 1 ? t('components.ticket:overtime.hour') : t('components.ticket:overtime.hours')} (${t('components.ticket:overtime.confirmed')})`;
                          return (
                            <span className="block truncate text-xs text-green-600 font-medium" title={text}>
                              {text}
                            </span>
                          );
                        }

                        const text = `${overtimeHours} ${overtimeHours === 1 ? t('components.ticket:overtime.hour') : t('components.ticket:overtime.hours')} (${t('components.ticket:overtime.unconfirmed')})`;
                        return (
                          <span className="block truncate text-xs text-orange-600 font-medium" title={text}>
                            {text}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-0.5 overflow-hidden whitespace-nowrap text-ellipsis text-right w-[140px] min-w-[140px] max-w-[140px]" style={{ fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfirmTicketDelete(ticket.id, ticket.ticketNumber || 'Unbekannt', ticket.title);
                          }}
                          className="list-delete-button"
                          title={t('pages.dashboard:table.actions.deleteTicket')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// Recipient Tickets Table Component
function RecipientTicketsTable() {
  const { t } = useTranslation(['pages.dashboard']);
  const [ticketSortBy, setTicketSortBy] = useState<string>("ticketNumber");
  const [ticketSortOrder, setTicketSortOrder] = useState<"asc" | "desc">("asc");

  const handleTicketSort = (column: string) => {
    if (ticketSortBy === column) {
      setTicketSortOrder(ticketSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setTicketSortBy(column);
      setTicketSortOrder('asc');
    }
  };

  return (
    <div>
      <div className="-mx-6 pl-4">
        <div className="w-full max-h-96 overflow-auto border-t border-gray-200">
          <table className="w-full min-w-[1200px] table-fixed">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="h-7 border-row px-4 text-left w-[4%]">
                  <button
                    onClick={() => handleTicketSort('ticketNumber')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.nr')}
                    {ticketSortBy === 'ticketNumber' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[12%]">
                  <span className="text-xs font-medium text-slate-700">{t('pages.dashboard:table.headers.project')}</span>
                </th>
                <th className="h-7 border-row px-1 text-left w-[15%]">
                  <button
                    onClick={() => handleTicketSort('title')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.title')}
                    {ticketSortBy === 'title' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[10%]">
                  <button
                    onClick={() => handleTicketSort('status')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.status')}
                    {ticketSortBy === 'status' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[8%]">
                  <button
                    onClick={() => handleTicketSort('priority')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.priority')}
                    {ticketSortBy === 'priority' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[9%]">
                  <button
                    onClick={() => handleTicketSort('gewerk')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.trade')}
                    {ticketSortBy === 'gewerk' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[9%]">
                  <span className="text-xs font-medium text-slate-700">{t('pages.dashboard:table.headers.location')}</span>
                </th>
                <th className="h-7 border-row px-1 text-left w-[11%]">
                  <button
                    onClick={() => handleTicketSort('assignee')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.assignee')}
                    {ticketSortBy === 'assignee' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[7%]">
                  <button
                    onClick={() => handleTicketSort('dueDate')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.due')}
                    {ticketSortBy === 'dueDate' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[7%]">
                  <button
                    onClick={() => handleTicketSort('costs')}
                    className="flex items-center justify-between w-full text-xs font-medium text-slate-700 hover:text-slate-900"
                  >
                    {t('pages.dashboard:table.headers.costs')}
                    {ticketSortBy === 'costs' && (
                      ticketSortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="h-7 border-row px-1 text-left w-[5%]">
                  <span className="text-xs font-medium text-slate-700">{t('pages.dashboard:table.headers.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 10 }).map((_, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
                >
                  <td className="h-7 border-row px-4 text-xs font-medium text-slate-900 w-[4%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-900 w-[12%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-900 w-[15%]"></td>
                  <td className="h-7 border-row text-xs w-[10%] bg-white"></td>
                  <td className="h-7 border-row px-1 text-xs w-[8%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-600 w-[9%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-600 w-[9%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-600 w-[11%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-500 w-[7%]"></td>
                  <td className="h-7 border-row px-1 text-xs text-slate-500 w-[7%]"></td>
                  <td className="h-7 border-row px-1 text-right w-[5%]"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
